import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Dropdown, 
  Spinner, 
  Alert 
} from 'react-bootstrap';
import MCQQuestionSession from './MCQQuestionSession';

const MCQMode = ({ 
  models, 
  selectedModel, 
  onModelSelect, 
  apiBaseUrl,
  selectedFireworkModel,
  setSelectedFireworkModel,
  questions,
  setQuestions,
  sessionActive,
  setSessionActive
}) => {
  const [studyMaterials, setStudyMaterials] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [difficulty, setDifficulty] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fireworkModels, setFireworkModels] = useState([]);

  // Fetch Firework models when models are loaded
  useEffect(() => {
    const fetchFireworkModels = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/models`);
        if (response.data.firework_models) {
          const fireworkModelsList = Object.entries(response.data.firework_models).map(([id, name]) => ({
            id,
            name
          }));
          setFireworkModels(fireworkModelsList);
        }
      } catch (err) {
        console.error('Error fetching Firework models:', err);
      }
    };
    
    if (models.length > 0) {
      fetchFireworkModels();
    }
  }, [models, apiBaseUrl]);

  const handleStudyMaterialsChange = (e) => {
    setStudyMaterials(Array.from(e.target.files));
  };

  const handleQuestionCountChange = (e) => {
    setQuestionCount(parseInt(e.target.value));
  };

  const handleDifficultySelect = (level) => {
    setDifficulty(level);
  };

  const handleFireworkModelSelect = (modelId) => {
    if (setSelectedFireworkModel) {
      setSelectedFireworkModel(modelId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (studyMaterials.length === 0) {
      setError('Please upload at least one study material file');
      return;
    }
    
    if (!selectedModel) {
      setError('Please select an AI model');
      return;
    }
    
    setLoading(true);
    setError('');
    setQuestions(null);
    setSessionActive(false);
    
    const formData = new FormData();
    studyMaterials.forEach(file => {
      formData.append('study_materials', file);
    });
    formData.append('question_count', questionCount);
    formData.append('ai_provider', selectedModel);
    formData.append('difficulty', difficulty);
    
    // Add firework_model parameter if Firework is selected
    if (selectedModel === 'firework' && selectedFireworkModel) {
      formData.append('firework_model', selectedFireworkModel);
    }
    
    try {
      // First check if backend is reachable
      try {
        await axios.get(`${apiBaseUrl}/health`);
      } catch (healthErr) {
        throw new Error(`Backend server unreachable. Please check if the backend service is running: ${healthErr.message}`);
      }
      
      // Calculate timeout based on file sizes
      const totalSizeMB = studyMaterials.reduce((total, file) => total + file.size, 0) / (1024 * 1024);
      const timeout = 600000; 
      
      // Show processing message for large files
      if (totalSizeMB > 5) {
        setError(`Processing ${Math.round(totalSizeMB)}MB of material, this may take several minutes. Please wait...`);
      }
      
      const response = await axios.post(`${apiBaseUrl}/generate-mcq`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: timeout
      });
      
      if (response.data && response.data.questions) {
        setQuestions(response.data.questions);
        setSessionActive(true);
        setError(''); // Clear any processing messages
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      
      // Handle different error scenarios with useful messages
      if (err.response) {
        // Server responded with an error status
        const serverMessage = err.response.data?.detail || JSON.stringify(err.response.data) || err.message;
        setError(`Server error (${err.response.status}): ${serverMessage}`);
      } else if (err.request) {
        // Request was made but no response received (timeout)
        if (err.code === 'ECONNABORTED') {
          setError(`Request timed out. Processing ${studyMaterials.length} files takes longer than expected. Try with fewer or smaller files.`);
        } else {
          setError('No response from server. Please check if the backend service is running.');
        }
      } else {
        // Error in request setup
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = () => {
    setSessionActive(false);
    // Note: We no longer clear the questions here to persist them
  };

  // Render practice session if active
  if (sessionActive && questions) {
    return (
      <MCQQuestionSession 
        questions={questions} 
        onEndSession={handleEndSession}
        selectedModel={selectedModel}
        selectedFireworkModel={selectedFireworkModel}
        apiBaseUrl={apiBaseUrl}
      />
    );
  }

  // Render the study material upload form
  return (
    <Card>
      <Card.Body>
        <Card.Title className="mb-4">Generate Multiple-Choice Questions</Card.Title>
        
        {error && <Alert variant={error.includes('Processing') ? "info" : "danger"}>{error}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="studyMaterials">
                <Form.Label>Upload Study Materials</Form.Label>
                <Form.Control 
                  type="file" 
                  multiple 
                  onChange={handleStudyMaterialsChange} 
                  accept=".pdf,.docx,.doc,.txt"
                />
                <Form.Text className="text-muted">
                  Upload lecture slides, notes, or other study materials (PDF, DOCX, DOC, or TXT)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="aiModel">
                <Form.Label>Select AI Model</Form.Label>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-primary" id="dropdown-model" className="w-100 text-start">
                    {models.find(m => m.id === selectedModel)?.name || 'Select AI Model'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {models.map(model => (
                      <Dropdown.Item 
                        key={model.id} 
                        onClick={() => onModelSelect(model.id)}
                        active={selectedModel === model.id}
                      >
                        {model.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group controlId="difficulty">
                <Form.Label>Difficulty Level</Form.Label>
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" id="dropdown-difficulty" className="w-100 text-start">
                    {difficulty === 'easy' ? 'Easy' : difficulty === 'medium' ? 'Medium' : 'Hard'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    <Dropdown.Item 
                      onClick={() => handleDifficultySelect('easy')}
                      active={difficulty === 'easy'}
                    >
                      Easy
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => handleDifficultySelect('medium')}
                      active={difficulty === 'medium'}
                    >
                      Medium
                    </Dropdown.Item>
                    <Dropdown.Item 
                      onClick={() => handleDifficultySelect('hard')}
                      active={difficulty === 'hard'}
                    >
                      Hard
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Show Firework model selection only when Firework is selected */}
          {selectedModel === 'firework' && fireworkModels.length > 0 && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="fireworkModel">
                  <Form.Label>Select OpenSource AI Model</Form.Label>
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="dropdown-firework-model" className="w-100 text-start">
                      {fireworkModels.find(m => m.id === selectedFireworkModel)?.name || 'Select AI Model'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100">
                      {fireworkModels.map(model => (
                        <Dropdown.Item 
                          key={model.id} 
                          onClick={() => handleFireworkModelSelect(model.id)}
                          active={selectedFireworkModel === model.id}
                        >
                          {model.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Form.Text className="text-muted">
                    Select a specific AI model for question generation
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="questionCount">
                <Form.Label>Number of Questions: {questionCount}</Form.Label>
                <Form.Range 
                  min={5} 
                  max={20} 
                  step={1} 
                  value={questionCount} 
                  onChange={handleQuestionCountChange} 
                />
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || studyMaterials.length === 0}
              size="lg"
            >
              {loading ? <><Spinner animation="border" size="sm" /> Generating Questions...</> : 'Generate MCQ Questions'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default MCQMode;